import React from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { Portal, Dialog as RNPDialog } from 'react-native-paper';

import { BigButton, Button, ButtonProps } from '@components/Button';
import { Paragraph } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { logError } from '@helpers/Loggers';

type DialogProps = React.ComponentProps<typeof RNPDialog>;

export default function Dialog(props: DialogProps) {
  return (
    <Portal>
      <RNPDialog {...props} />
    </Portal>
  );
}

type ConfirmDeleteDialogProps = {
  onCancel: () => void;
  onDelete: () => void;
  title?: string;
  message?: string;
} & Omit<DialogProps, 'children' | 'onDismiss'>;

export function ConfirmDeleteDialog({
  onCancel,
  onDelete,
  title,
  message,
  style,
  ...props
}: ConfirmDeleteDialogProps) {
  const appTheme = useAppTheme();

  return (
    <Portal>
      <RNPDialog
        onDismiss={onCancel}
        style={[
          styles.confirmDeleteContainer,
          { width: 0.8 * Math.min(Dimensions.get('window').width, appTheme.maxWidth) },
          style,
        ]}
        {...props}>
        <RNPDialog.Title>{title || 'Confirm Deletion'}</RNPDialog.Title>
        <RNPDialog.Content>
          <Paragraph>
            {message || "Are you sure you want to delete? This action can't be undone."}
          </Paragraph>
        </RNPDialog.Content>
        <RNPDialog.Actions style={styles.confirmDeleteActionsContainer}>
          <Button
            onPress={onCancel}
            mode="contained"
            style={styles.confirmDeleteButton}
            testID="components.Dialog.ConfirmDeleteDialog.Cancel">
            Cancel
          </Button>
          <Button
            onPress={onDelete}
            mode="text"
            style={styles.confirmDeleteButton}
            testID="components.Dialog.ConfirmDeleteDialog.Delete">
            Delete
          </Button>
        </RNPDialog.Actions>
      </RNPDialog>
    </Portal>
  );
}

type ConfirmDeleteButtonProps = {
  /** Awaited so the button can reflect the delete lifecycle. */
  onDelete: () => Promise<void>;
  message?: string;
} & Omit<ButtonProps, 'onPress' | 'loading' | 'disabled'>;

export function ConfirmDeleteDialogButton({
  onDelete,
  message,
  ...props
}: ConfirmDeleteButtonProps) {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [disabled, setDisabled] = React.useState<boolean>(false);

  const _onDelete = async () => {
    setDisabled(true);
    setLoading(true);
    setShowDeleteDialog(false);
    try {
      await onDelete();
    } catch (error) {
      logError(error);
    }
    setDisabled(false);
    setLoading(false);
  };

  return (
    <View>
      <BigButton
        onPress={() => setShowDeleteDialog(true)}
        loading={loading}
        disabled={disabled}
        {...props}>
        Delete
      </BigButton>
      <ConfirmDeleteDialog
        visible={showDeleteDialog}
        onCancel={() => setShowDeleteDialog(false)}
        onDelete={_onDelete}
        message={message}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  confirmDeleteActionsContainer: {
    justifyContent: 'space-evenly',
  },
  confirmDeleteButton: {
    marginBottom: 10,
  },
  confirmDeleteContainer: {
    alignSelf: 'center',
  },
});
